/* eslint-disable react/no-array-index-key */

import React, {useEffect, useCallback, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import cN from 'classnames';

import MarkDown from 'scripts/components/MarkDown';
import {hyphenize} from 'utils/text';

import Slider from './Slider';


const ROTATION_ANGLE = 56;

const ChoicesHeader = ({choices, rotatedHeight, onRotatedHeightChange}) => {
    const choiceRefs = useRef({});

    const addChoiceRef = useCallback((node, idx) => {
        choiceRefs.current[idx] = node;
    }, []);

    useEffect(() => {
        const max_size = Object.keys(choiceRefs.current).reduce((cur, idx) => {
            const node = choiceRefs.current[idx];

            if (node) {
                const textSize = node.clientWidth;

                return textSize > cur ? textSize : cur;
            }
        }, 0);

        onRotatedHeightChange(max_size * Math.cos(ROTATION_ANGLE));
    }, []);

    return (
        <div className="choices rotate" style={{height: rotatedHeight ? `${rotatedHeight}px` : null}}>
            {choices.map((({choice}, idx) => (
                <div key={idx} className="choice">
                    <span ref={node => addChoiceRef(node, idx)} className="choice-text">{hyphenize(choice)}</span>
                </div>
            )))}
        </div>
    );
};


const Table = ({config, answer, missingInfo, id, disabled, updateAnswer}) => {
    const {choices = [], questions = [], control, min_label, center_label, max_label} = config;
    const {choices: answerChoices = []} = answer;

    const [rotatedHeight, setRotatedHeight] = useState(0);

    const handleRotatedHeightChange = useCallback(height => setRotatedHeight(height), []);

    const onChange = useCallback((idx, val) => {
        const choices = [...answerChoices];
        choices[idx] = val;
        return updateAnswer({choices});
    }, [answerChoices, updateAnswer]);

    return (
        <div className="table-block">
            <div className="flex-table">
                <div className="flex-table-row flex-table-header">
                    <div className="question" />
                    {control === 'choice' && (
                        <ChoicesHeader
                            choices={choices}
                            rotatedHeight={rotatedHeight}
                            onRotatedHeightChange={handleRotatedHeightChange}
                        />
                    )}
                    {control === 'slider' && (
                        <div className="slider-choices">
                            {min_label && <span>{hyphenize(min_label)}</span>}
                            {center_label && <span>{hyphenize(center_label)}</span>}
                            {max_label && <span>{hyphenize(max_label)}</span>}
                        </div>
                    )}
                </div>
                {questions.map(({question, value}, questionIndex) => (
                    <div key={questionIndex} className={cN('flex-table-row', {missing: missingInfo && missingInfo.includes(questionIndex)})}>
                        <div className="question"><MarkDown text={hyphenize(question)} /></div>
                        <div className="choices rotate">
                            {control === 'choice' && choices.map(({choice}, choiceIndex) => (
                                <div className="choice" key={choiceIndex}>
                                    <div>
                                        <input
                                            id={`choice-${id}-${questionIndex}-${choiceIndex}`}
                                            name={`question-${id}-${questionIndex}`}
                                            value={choiceIndex}
                                            type="radio"
                                            onChange={() => onChange(questionIndex, choiceIndex)}
                                            checked={answerChoices && answerChoices[questionIndex] === choiceIndex}
                                            disabled={disabled}
                                        />
                                        <label htmlFor={`choice-${id}-${questionIndex}-${choiceIndex}`} />
                                    </div>
                                </div>
                            ))}
                            {control === 'slider' && (
                                <div className="choice">
                                    <Slider
                                        config={{
                                            ...config,
                                            center_label: '',
                                            min_label: '',
                                            max_label: '',
                                        }}
                                        answer={{value: answerChoices[questionIndex]}}
                                        updateAnswer={({value}) => onChange(questionIndex, value)}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};


Table.canContinue = (answer, config) => {
    const {choices = []} = answer;
    const {questions = [], answer_optional: isAnswerOptional} = config;

    if (isAnswerOptional) {
        return false;
    }

    const missing = [];

    questions.forEach((_, idx) => {
        if (typeof choices[idx] !== 'number') missing.push(idx);
    });

    return missing.length ? missing : false;
};


Table.propTypes = {
    config: PropTypes.shape({
        choices: PropTypes.array.isRequired,
    }).isRequired,
    answer: PropTypes.shape({
        choice: PropTypes.number,
    }).isRequired,
    disabled: PropTypes.bool,
    updateAnswer: PropTypes.func.isRequired,
    id: PropTypes.number,
};

Table.defaultProps = {
    disabled: false,
    id: 0,
};

export default Table;



// WEBPACK FOOTER //
// ./scripts/frontend/lessons/components/Table.js